<template>
  <vuetify-wrapper>
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col">
          <div class="row mb-2">
            <!-- Exportar -->
            <div class="col-md-8">
              <vue-excel-xlsx
                class="btn btn-primary btn-sm mt-4"
                :data="data"
                :columns="headers.map(header => ({
                  label: header.text,
                  field: header.value
                }))"
                :file-name="`${name || 'Relatório'}`"
                sheet-name="Dados"
              >
                Exportar Excel
                <i class="fas fa-file-excel ml-1"></i>
              </vue-excel-xlsx>
            </div>
            
            <!-- Pesquisa -->
            <div class="col-md-4" v-if="pesquisa">
              <div class="form-group mb-0">
                <label for="search-input" class="form-label small">Buscar:</label>
                <input
                  id="search-input"
                  type="text"
                  v-model="search"
                  class="form-control form-control-sm"
                  placeholder="Digite para buscar..."
                >
              </div>
            </div>
          </div>

          <!-- Data Table -->
          <v-data-table
            :headers="headers"
            :items="data"
            :search="search"
            no-data-text="Nenhum dado encontrado"
            class="elevation-1"
            :options.sync="localOptions"
            hide-default-footer
            hide-default-header
            :sort-by.sync="sort"
            :sort-desc.sync="sort_desc"
            :custom-sort="customSort"
          >
            <!-- Forward per-column item slots with fallback text rendering -->
            <template v-for="header in headers" v-slot:[`item.${header.value}`]="slotProps">
              <slot :name="`item.${header.value}`" v-bind="slotProps">
                {{ slotProps.item[header.value] }}
              </slot>
            </template>
            <template #header>
              <thead class="v-data-table-header">
                <tr>
                  <th 
                    v-for="header in headers" 
                    :key="header.value"
                    @click="header.sortable !== false ? sortColumn(header.value) : null"
                    :style="{ cursor: header.sortable !== false ? 'pointer' : 'default' }"
                    :class="{ 'sortable': header.sortable !== false }"
                  >
                    <span class="d-flex justify-content-between align-items-center">
                      {{ header.text }}
                      <span v-if="header.sortable !== false" class="ml-1">
                        <span v-show="sort === header.value && !sort_desc">
                          <i class='fa fa-arrow-up'></i>
                        </span>
                        <span v-show="sort === header.value && sort_desc">
                          <i class='fa fa-arrow-down'></i>
                        </span>
                      </span>
                      <span
                        v-if="header.info"
                        class="ml-1"
                        data-toggle="tooltip"
                        data-placement="top"
                        data-html="true"
                        :title="header.info"
                      >
                        <i class="fas fa-question-circle mr-1"></i>
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
            </template>
            <template #[`body.append`]="{ headers }">
                <tr class="v-row-group__header total">
                  <td class="text-right" v-for="(item, index) in headers" :key="index">
                    {{ sumariza(item) }}
                  </td>
                </tr>
              </template>
          </v-data-table>

          <!-- Footer -->
          <div class="d-flex justify-content-between align-items-center mt-3">
            <div>
              <p class="small">Exibindo {{ startItem }} a {{ endItem }} de {{ totalItems }} resultados</p>
            </div>

            <!-- Paginacao -->
            <nav aria-label="Navegação da tabela" v-if="totalPages > 1">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: localOptions.page === 1 }">
                  <button 
                    class="page-link" 
                    @click="goToPage(1)"
                    :disabled="localOptions.page === 1"
                  >
                    &laquo;&laquo;
                  </button>
                </li>
                <li class="page-item" :class="{ disabled: localOptions.page === 1 }">
                  <button 
                    class="page-link" 
                    @click="goToPage(localOptions.page - 1)"
                    :disabled="localOptions.page === 1"
                  >
                    &laquo;
                  </button>
                </li>
                
                <li 
                  v-for="(page, index) in visiblePages" 
                  :key="page === '...' ? `ellipsis-${index}` : page"
                  class="page-item" 
                  :class="{ active: page === localOptions.page, disabled: page === '...' }"
                >
                  <button 
                    class="page-link" 
                    @click="page !== '...' ? goToPage(page) : null"
                    :disabled="page === '...'"
                  >
                    {{ page }}
                  </button>
                </li>
                
                <li class="page-item" :class="{ disabled: localOptions.page === totalPages }">
                  <button 
                    class="page-link" 
                    @click="goToPage(localOptions.page + 1)"
                    :disabled="localOptions.page === totalPages"
                  >
                    &raquo;
                  </button>
                </li>
                <li class="page-item" :class="{ disabled: localOptions.page === totalPages }">
                  <button 
                    class="page-link" 
                    @click="goToPage(totalPages)"
                    :disabled="localOptions.page === totalPages"
                  >
                    &raquo;&raquo;
                  </button>
                </li>
              </ul>
            </nav>

            <!-- Itens por página -->
            <div class="form-inline">
              <label class="mr-2">Itens por página:</label>
              <select 
                v-model="localOptions.itemsPerPage" 
                class="form-control form-control-sm"
                @change="onItemsPerPageChange"
              >
                <option 
                  v-for="option in itensPerPageOptions" 
                  :key="option" 
                  :value="option"
                >
                  {{ option === -1 ? 'Todos' : option }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </vuetify-wrapper>
</template>

<script>
import numeral from 'numeral';

export default {
  name: 'DataTable',
  props: {
    id: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    headers : {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    itensPerPageOptions: {
      type: Array,
      default: () => [10, 20, 50, -1],
    },
    options: {
      type: Object,
      default: () => ({
        page: 1,
        itemsPerPage: 10,
      }),
    },
    pesquisa: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localOptions: { ...this.options },
      search: '',
      sort: '',
      sort_desc: '',
    };
  },
  computed: {
    totalItems() {
      return this.data.length;
    },
    totalPages() {
      if (this.localOptions.itemsPerPage === -1) {
        return 1;
      }
      return Math.ceil(this.totalItems / this.localOptions.itemsPerPage);
    },
    startItem() {
      if (this.totalItems === 0) return 0;
      if (this.localOptions.itemsPerPage === -1) return 1;
      return (this.localOptions.page - 1) * this.localOptions.itemsPerPage + 1;
    },
    endItem() {
      if (this.totalItems === 0) return 0;
      if (this.localOptions.itemsPerPage === -1) return this.totalItems;
      const end = this.localOptions.page * this.localOptions.itemsPerPage;
      return end > this.totalItems ? this.totalItems : end;
    },
    visiblePages() {
      const pages = [];
      const current = this.localOptions.page;
      const total = this.totalPages;
      
      // Se há 7 ou menos páginas, mostra todas
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.localOptions.page = page;
      }
    },
    onItemsPerPageChange() {
      this.localOptions.page = 1;
    },
    sortColumn(column) {
      if (this.sort === column) {
        if (!this.sort_desc) {
          this.sort_desc = true;
        } else {
          this.sort = '';
          this.sort_desc = false;
        }
      } else {
        this.sort = column;
        this.sort_desc = false;
      }
    },
    sumariza(coluna) {
      if (this.data) {
        if (coluna.total) {
          const total = this.data.reduce((a, b) => {
            const value = b[coluna.value];
            if (value == null || value === undefined) return a;
            const val = value.toString().replaceAll('.', '').replace(',', '.');
            return a + parseFloat(val);
          }, 0);
            const precision = coluna.precision !== undefined ? coluna.precision : 2;
          return numeral(total).format(`0,0.${'0'.repeat(precision)}`).replace(/,/g, 'TEMP').replace(/\./g, ',').replace(/TEMP/g, '.');
        }
        if (coluna.media) {
          const sum = this.data.reduce((a, b) => {
            const value = b[coluna.value];
            if (value == null || value === undefined) return a;
            const val = value.toString().replaceAll('.', '').replace(',', '.');
            return a + parseFloat(val);
          }, 0);
          const average = sum / this.data.length;
          const precision = coluna.precision !== undefined ? coluna.precision : 2;
          return numeral(average).format(`0,0.${'0'.repeat(precision)}`).replace(/,/g, 'TEMP').replace(/\./g, ',').replace(/TEMP/g, '.');
        }
      }
      return '';
    },
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        let valora = a[index];
        let valorb = b[index];

        // Tratar valores nulos, undefined ou vazios
        if (valora == null || valora === '' || valora === undefined) valora = '';
        if (valorb == null || valorb === '' || valorb === undefined) valorb = '';

        // Se ambos estão vazios, são iguais
        if (valora === '' && valorb === '') return 0;

        // Valores vazios vão para o final
        if (valora === '' && valorb !== '') return isDesc[0] ? -1 : 1;
        if (valorb === '' && valora !== '') return isDesc[0] ? 1 : -1;

        // Verificar se é data (DD/MM/YYYY)
        if (valorb && valorb.length == 10 && valorb.indexOf('/') >= 0
            && valora && valora.length == 10 && valora.indexOf('/') >= 0) {
          valora = moment(valora, 'DD/MM/YYYY');
          valorb = moment(valorb, 'DD/MM/YYYY');
        } else if (valorb && typeof valorb == 'string' && valorb.indexOf(',') >= 0
                   && valora && typeof valora == 'string') {
          // Fazer os replaces apenas se os valores não estão vazios
          valorb = valorb.replace(/\./g, '').replace(',', '.');
          valora = valora.replace(/\./g, '').replace(',', '.');

          if (this.checkNumber(valora) && this.checkNumber(valorb)) {
            valorb = parseFloat(valorb);
            valora = parseFloat(valora);
          }
        } else if (Number(valorb) == valorb && Number(valora) == valora) {
          valora = Number(valora);
          valorb = Number(valorb);
        }

        if (!isDesc[0]) {
          return (valora < valorb) ? -1 : 1;
        }
        return (valorb < valora) ? -1 : 1;
      });
      return items;
    },
    checkNumber(valor) {
      return Number(valor) == valor && valor.toString().indexOf('.') != -1;
    },
  },
  watch: {
    options: {
      handler(newVal) {
        this.localOptions = { ...newVal };
      },
      deep: true,
    },
    localOptions: {
      handler(newVal) {
        this.$emit('update:options', newVal);
      },
      deep: true,
    },
  },
};
</script>
